import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import jardim01 from "../assets/images/jardim01.jpg";
import jardim02 from "../assets/images/jardim02.jpg";
import jardim03 from "../assets/images/jardim03.jpg";
import cemiterio01 from "../assets/images/cemiterio01.jpg";
import cemiterio02 from "../assets/images/cemiterio02.jpg";
import columbario01 from "../assets/images/columbario01.jpg";
import columbario02 from "../assets/images/columbario02.jpg";
import columbario03 from "../assets/images/columbario03.jpg";
import columbario04 from "../assets/images/columbario04.jpg";
import estrutura01 from "../assets/images/estrutura01.jpg";
import gavetas01 from "../assets/images/gavetas01.jpg";
import convencional01 from "../assets/images/cemiterio-convencional.jpg";
import convencionalAnimais from "../assets/images/cemiterio-animais.jpg";

const CemiteriosContainer = styled.div`
  color: var(--primary-blue);
  padding-top: 80px;
  min-height: 100vh;
  @media screen and (max-width: 768px) {
    padding-top: 40px;
  }
`;

const CemiteriosWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px 40px 24px;
`;

const TopLine = styled(motion.p)`
  color: #4CAF50;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin-bottom: 16px;
  font-family: 'Open Sans', sans-serif;
`;

const Heading = styled(motion.h2)`
  margin-bottom: 24px;
  font-size: 40px;
  font-weight: 600;
  color: var(--primary-blue);
  font-family: 'Playfair Display', serif;
  @media screen and (max-width: 480px) {
    font-size: 32px;
  }
`;

const Subtitle = styled(motion.p)`
  max-width: 700px;
  margin-bottom: 60px;
  font-size: 18px;
  line-height: 24px;
  color: var(--primary-blue);
  font-family: 'Open Sans', sans-serif;
`;

const CemiterioSectionWrapper = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: ${({ $even }) => $even ? '#fff' : 'var(--primary-blue)'};
  transition: background 0.3s;
`;

const CemiterioSection = styled(motion.section)`
  margin-bottom: 0;
  padding: 40px 0;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
`;

const CemiterioTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 20px;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Playfair Display', serif;
  @media screen and (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CemiterioContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const CemiterioText = styled.div`
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
`;

const CemiterioDesc = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 20px;
`;

const CemiterioFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const CemiterioFeature = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ $even }) => $even ? 'var(--primary-blue)' : '#fff'};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4CAF50;
    font-weight: bold;
  }
`;

const CemiterioImages = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CemiterioImg = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const CemiterioImgLarge = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Cemiterios = () => {
  const sections = [
    {
      title: 'Cemitério Jardin',
      desc: 'O Cemitério Jardin oferece uma experiência única de conexão com a natureza. Este espaço ao ar livre foi projetado para proporcionar serenidade e beleza, permitindo que as famílias encontrem paz e tranquilidade durante as visitas.',
      features: [
        'Paisagismo natural e harmonioso',
        'Ambiente sereno e acolhedor',
        'Áreas sombreadas para conforto',
        'Manutenção constante da vegetação',
        'Acesso facilitado para visitantes'
      ],
      images: [cemiterio01]
    },
    {
      title: 'Cemitério Convencional',
      desc: 'O Cemitério Convencional respeita as tradições e costumes familiares, oferecendo sepulturas tradicionais com toda a infraestrutura necessária. Este espaço mantém a solenidade e o respeito que as famílias esperam em momentos de despedida.',
      features: [
        'Sepulturas tradicionais',
        'Infraestrutura completa e acessível',
        'Áreas de velório integradas',
        'Capela para cerimônias',
        'Estacionamento amplo'
      ],
      images: [convencional01]
    },
    {
      title: 'Galeria com Gavetas',
      desc: 'A Galeria com Gavetas representa uma solução moderna e prática para o sepultamento. Com nichos organizados verticalmente, esta opção otimiza o espaço disponível e facilita a visitação, oferecendo conforto e praticidade às famílias.',
      features: [
        'Nichos organizados verticalmente',
        'Otimização de espaço',
        'Facilitação da visitação',
        'Estrutura moderna e segura',
        'Manutenção simplificada'
      ],
      images: [gavetas01]
    },
    {
      title: 'Espaço Perpétuo',
      desc: 'O Espaço Perpétuo foi concebido para a perpetuidade da memória, oferecendo uma estrutura diferenciada e um ambiente reservado para homenagens duradouras. Este espaço especial permite que as famílias mantenham viva a lembrança de seus entes queridos de forma permanente e respeitosa.',
      features: [
        'Estrutura diferenciada e exclusiva',
        'Ambiente reservado para homenagens',
        'Perpetuidade da memória',
        'Design arquitetônico especial',
        'Área de contemplação'
      ],
      images: [columbario04]
    },
    {
      title: 'Columbário',
      desc: 'O Columbário oferece uma solução elegante e respeitosa para a guarda de urnas de cremação. Esta estrutura foi projetada com foco na segurança, privacidade e respeito à memória dos entes queridos, proporcionando um local digno para a preservação das cinzas.',
      features: [
        'Estrutura para urnas de cremação',
        'Segurança e privacidade',
        'Ambiente climatizado',
        'Acesso controlado',
        'Manutenção especializada'
      ],
      images: [columbario03]
    },
    {
      title: 'Cemitério de Pequenos Animais',
      desc: 'O Cemitério de Pequenos Animais oferece um espaço especial para homenagear e eternizar a memória dos animais de estimação. Com carinho e respeito, este local permite que as famílias se despeçam de seus companheiros de forma digna e amorosa.',
      features: [
        'Espaço dedicado aos animais',
        'Capela para cerimônias',
        'Serviço de cremação',
        //'Memorial para recordações',
        'Tratamento com carinho e respeito',
        'Ambiente acolhedor',
        //'Homenagens personalizadas',
      ],
      images: [convencionalAnimais]
    }
  ];

  return (
    <CemiteriosContainer>
      <CemiteriosWrapper>
        <TopLine initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          Cemitérios
        </TopLine>
        <Heading initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
          Opções de Cemitérios Dom José
        </Heading>
        <Subtitle initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
          Dentro do Complexo Memorial Crematório e Cemitério Dom José, existem seis alternativas para guardar os restos mortais do seu ente querido. Cada opção foi cuidadosamente planejada para oferecer conforto, respeito e tranquilidade às famílias.
        </Subtitle>
        {sections.map((section, idx) => (
          <CemiterioSectionWrapper key={section.title} $even={idx % 2 !== 0}>
            <CemiterioSection initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 * idx }}>
              <CemiterioTitle $even={idx % 2 !== 0}>{section.title}</CemiterioTitle>
              <CemiterioContent>
                <CemiterioText $even={idx % 2 !== 0}>
                  <CemiterioDesc $even={idx % 2 !== 0}>{section.desc}</CemiterioDesc>
                  <CemiterioFeatures>
                    {section.features.map((f, i) => (
                      <CemiterioFeature key={i} $even={idx % 2 !== 0}>{f}</CemiterioFeature>
                    ))}
                  </CemiterioFeatures>
                </CemiterioText>
                <CemiterioImages>
                  {section.images.map((img, i) => (
                    <CemiterioImg key={i} src={img} alt={section.title + ' imagem ' + (i+1)} />
                  ))}
                </CemiterioImages>
              </CemiterioContent>
            </CemiterioSection>
          </CemiterioSectionWrapper>
        ))}
      </CemiteriosWrapper>
    </CemiteriosContainer>
  );
};

export default Cemiterios; 